/**
 * UTM Parameter Tracking
 * Captures UTM parameters from URL and stores them in cookies
 * Compatible with GDPR/Cookie consent requirements
 */
class UTMTracker {
    constructor(options = {}) {
        this.options = {
            cookiePrefix: 'ns_',
            cookieDuration: 30, // days
            cookiePath: '/',
            cookieDomain: window.location.hostname,
            trackingParams: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gad_campaignid'],
            // Additional parameters you might want to track
            additionalParams: ['gclid', 'fbclid', 'gad_source'],
            // Attribution strategy: 'first' (first-touch) or 'last' (last-touch)
            attribution: 'first',
            // Whether to require consent before setting cookies
            requireConsent: false,
            ...options
        };
    }

    /**
     * Initialize UTM tracking
     */
    init() {
        // Wait for potential cookie consent
        if (this.options.requireConsent) {
            this.waitForConsent(() => {
                this.captureUTMParameters();
            });
        } else {
            this.captureUTMParameters();
        }
    }

    /**
     * Wait for cookie consent (compatible with Cookie Pro)
     */
    waitForConsent(callback) {
        // Check if Cookie Pro is available
        if (typeof OneTrust !== 'undefined') {
            OneTrust.OnConsentChanged(() => {
                if (this.hasAnalyticsConsent()) {
                    callback();
                }
            });

            // Also check immediately in case consent was already given
            if (this.hasAnalyticsConsent()) {
                callback();
            }
        } else {
            // Fallback: wait a bit and then proceed
            setTimeout(() => {
                if (this.hasAnalyticsConsent()) {
                    callback();
                }
            }, 1000);
        }
    }

    /**
     * Check if analytics consent is given
     */
    hasAnalyticsConsent() {
        // Check Cookie Pro consent
        if (typeof OneTrust !== 'undefined') {
            return OneTrust.IsAlertBoxClosed() && OneTrust.getGeolocationData().country !== 'UNKNOWN';
        }

        // Check GTM consent state
        if (typeof gtag !== 'undefined') {
            // This is a simplified check - you might need to adjust based on your consent setup
            return true; // Fallback to true if we can't determine consent
        }

        return !this.options.requireConsent;
    }

    /**
     * Capture UTM parameters from current URL
     */
    captureUTMParameters() {
        const urlParams = new URLSearchParams(window.location.search);
        const allParams = [...this.options.trackingParams, ...this.options.additionalParams];

        allParams.forEach(param => {
            const value = urlParams.get(param);
            if (value) {
                this.setUTMCookie(param, value);
            }
        });

        // Capture referrer information
        if (document.referrer) {
            const referrerDomain = this.extractDomain(document.referrer);
            const referrerCookie = this.options.cookiePrefix + 'referrer_url';

            // Always store the full referrer URL (first-touch)
            if (!this.getCookie(referrerCookie)) {
                this.setCookie(referrerCookie, document.referrer, this.options.cookieDuration);
            }

            // Set utm_source/medium if no UTM params are present
            if (!urlParams.get('utm_source') && referrerDomain && referrerDomain !== window.location.hostname) {
                this.setUTMCookie('utm_source', referrerDomain);
                this.setUTMCookie('utm_medium', 'referral');

                // Try to extract search terms from referrer
                const searchTerms = this.extractSearchTerms(document.referrer);
                if (searchTerms) {
                    this.setUTMCookie('utm_term', searchTerms);
                }
            }
        }

                        // Set visit timestamps (formatted for ActiveCampaign)
        const currentDate = new Date();
        const dateString = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        const firstVisitCookie = this.options.cookiePrefix + 'first_visit';
        const lastVisitCookie = this.options.cookiePrefix + 'last_visit';
        const isFirstVisit = !this.getCookie(firstVisitCookie);

        if (isFirstVisit) {
            // For first visit, both dates should be the same
            this.setCookie(firstVisitCookie, dateString, this.options.cookieDuration);
            this.setCookie(lastVisitCookie, dateString, this.options.cookieDuration);
        } else {
            // For subsequent visits, only update last_visit
            this.setCookie(lastVisitCookie, dateString, this.options.cookieDuration);
        }
    }

    /**
     * Set UTM cookie with attribution logic
     */
    setUTMCookie(param, value) {
        const cookieName = this.options.cookiePrefix + param;
        const existingValue = this.getCookie(cookieName);

        // Apply attribution strategy
        if (this.options.attribution === 'first' && existingValue) {
            // Keep first value, don't overwrite
            return;
        }

        // For 'last' attribution or no existing value, set the new value
        this.setCookie(cookieName, value, this.options.cookieDuration);
    }

    /**
     * Get all UTM parameters from cookies
     */
    getUTMParameters() {
        const params = {};
        const allParams = [...this.options.trackingParams, ...this.options.additionalParams];

        allParams.forEach(param => {
            const value = this.getCookie(this.options.cookiePrefix + param);
            if (value) {
                params[param] = value;
            }
        });

                                // Add visit dates (already in YYYY-MM-DD format)
        const firstVisit = this.getCookie(this.options.cookiePrefix + 'first_visit');
        const lastVisit = this.getCookie(this.options.cookiePrefix + 'last_visit');
        const referrerUrl = this.getCookie(this.options.cookiePrefix + 'referrer_url');

        if (firstVisit) params.first_visit = firstVisit;
        if (lastVisit) params.last_visit = lastVisit;
        if (referrerUrl) params.referrer_url = referrerUrl;

        return params;
    }



    /**
     * Send UTM data to Google Analytics
     */
    sendToGA() {
        if (typeof gtag !== 'undefined') {
            const params = this.getUTMParameters();

            gtag('event', 'utm_tracking', {
                custom_map: params,
                utm_source: params.utm_source,
                utm_medium: params.utm_medium,
                utm_campaign: params.utm_campaign
            });
        }
    }

    /**
     * Generic cookie setter
     */
    setCookie(name, value, days) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=${this.options.cookiePath}; domain=${this.options.cookieDomain}; SameSite=Lax`;
    }

    /**
     * Generic cookie getter
     */
    getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');

        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) {
                return decodeURIComponent(c.substring(nameEQ.length, c.length));
            }
        }
        return null;
    }

    /**
     * Clear all UTM cookies
     */
    clearUTMCookies() {
        const allParams = [...this.options.trackingParams, ...this.options.additionalParams];

        allParams.forEach(param => {
            const cookieName = this.options.cookiePrefix + param;
            this.setCookie(cookieName, '', -1);
        });

        this.setCookie(this.options.cookiePrefix + 'first_visit', '', -1);
        this.setCookie(this.options.cookiePrefix + 'last_visit', '', -1);
        this.setCookie(this.options.cookiePrefix + 'referrer_url', '', -1);
    }

    /**
     * Extract domain from URL
     */
    extractDomain(url) {
        try {
            return new URL(url).hostname;
        } catch (e) {
            return null;
        }
    }

    /**
     * Extract search terms from referrer URL
     */
    extractSearchTerms(referrerUrl) {
        try {
            const url = new URL(referrerUrl);
            const hostname = url.hostname.toLowerCase();

            // Common search engines and their query parameters
            const searchEngines = {
                'google.com': 'q',
                'google.nl': 'q',
                'google.de': 'q',
                'bing.com': 'q',
                'yahoo.com': 'p',
                'duckduckgo.com': 'q',
                'ecosia.org': 'q',
                'oogvitamines.ddev.site': 'q',
                'startpage.com': 'query'
            };

            // Check if referrer is from a known search engine
            const searchEngine = Object.keys(searchEngines).find(engine =>
                hostname.includes(engine)
            );

            if (searchEngine) {
                const queryParam = searchEngines[searchEngine];
                const searchParams = new URLSearchParams(url.search);
                const rawSearchTerm = searchParams.get(queryParam);

                if (rawSearchTerm) {
                    // Decode URL encoding and replace + with spaces
                    return decodeURIComponent(rawSearchTerm.replace(/\+/g, ' '));
                }

                return null;
            }

            return null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Debug: log all UTM data to console
     */
    debug() {
        console.group('UTM Tracking Debug');
        console.log('Current URL params:', new URLSearchParams(window.location.search));
        console.log('Stored UTM data:', this.getUTMParameters());
        console.log('Consent status:', this.hasAnalyticsConsent());
        console.groupEnd();
    }
}

// Initialize UTM tracking when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Initialize with your preferred settings
    window.utmTracker = new UTMTracker({
        cookieDuration: 30, // 30 days
        attribution: 'first', // Keep first-touch attribution
        requireConsent: false // Wait for cookie consent
    });

    window.utmTracker.init();
});

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
    module.exports = UTMTracker;
}